import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Sprout } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/");
    else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F9F8F6]">
      <div
        className="hidden md:block relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1765370817530-346a87971414?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwYWJzdHJhY3QlMjB3YXJtJTIwc2FuZCUyMHRleHR1cmV8ZW58MHx8fHwxNzc4MDgzMDIyfDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3328]/40 via-transparent to-[#E07A5F]/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="label-kicker text-white/80">A quiet space for</p>
          <h2 className="font-display text-4xl font-medium mt-3 leading-tight">
            Clear debts.<br /> Calm mind.
          </h2>
          <p className="mt-4 text-sm text-white/80 max-w-sm">
            Track installments, interest and payments without the noise. Watch the balance melt
            toward zero.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#2C4A3B] flex items-center justify-center text-white">
              <Sprout className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div>
              <div className="font-display text-xl font-medium text-[#1D3328]">Quietus</div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8B9389]">Debt Tracker</div>
            </div>
          </div>

          <p className="label-kicker">Welcome back</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
            Sign in
          </h1>
          <p className="mt-3 text-[#5C635A]">Pick up where you left off.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1D3328] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border-[#E5E3DB] h-11 bg-white"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#1D3328] font-medium">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#2C4A3B] font-medium underline underline-offset-4 hover:text-[#1D3328]"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl border-[#E5E3DB] h-11 bg-white"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-[#B04A32] bg-[#F7E6DF] border border-[#E07A5F]/30 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white font-medium transition-all hover:-translate-y-0.5"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[#5C635A]">
            New here?{" "}
            <Link to="/register" className="text-[#2C4A3B] font-medium underline underline-offset-4" data-testid="go-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
